import { Calendar, Package, Ticket, ShoppingBag, Gift, ExternalLink } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@baluu/ui/components/card";
import { Button } from "@baluu/ui/components/button";
import { TabSkeleton, TabError } from "../TabStatus";
import { formatDateWithWeekday, formatTime, formatRelative, escapeICalText } from "../format";
import type { PurchaseRow } from "../types";
import type { ManageUrl } from "../Profile";
import { getBundleStatus, BundleProgressRow } from "./BundlesTab";

/**
 * Overview tab — stats grid + upcoming sessions + active bundles progress.
 * Ported from ProfileView.tsx StatsGrid (:561-617), UpcomingSessionsCard
 * (:476-558) and the Overview TabsContent (:1039-1048). The hosted version's
 * "upcoming sessions" filter also excludes `booking.cancelled` /
 * `booking.session?.cancelled` — `PurchaseRow` (this app's /widget/me/purchases
 * shape, see ../types.ts) carries neither field, so this only filters on
 * `eventDate` being in the future.
 */

function generateGoogleCalendarUrl(row: PurchaseRow): string {
  const start = new Date(row.eventDate ?? "");
  const end = new Date(start.getTime() + 60 * 60 * 1000);
  const fmt = (d: Date) => d.toISOString().replace(/[-:]|\.\d{3}/g, "");
  const text = encodeURIComponent(row.title ?? "Booking");
  const details = encodeURIComponent("Booked via your profile");
  return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${text}&dates=${fmt(start)}/${fmt(end)}&details=${details}`;
}

function downloadICS(row: PurchaseRow) {
  const start = new Date(row.eventDate ?? "");
  const end = new Date(start.getTime() + 60 * 60 * 1000);
  const fmt = (d: Date) => d.toISOString().replace(/[-:]|\.\d{3}/g, "");
  // Every free-text value goes through `escapeICalText` — the title because it
  // is school-controlled input and a newline in it would end the SUMMARY
  // property and let the rest of the string inject its own iCal properties or
  // whole extra VEVENTs into the file the customer imports (RFC 5545 §3.3.11);
  // the description because it is a TEXT value too, and leaving one raw is how
  // the next edit reintroduces the hole. The DTSTART/DTEND values are not TEXT
  // (they are the formatter's own digits) and must NOT be escaped.
  const summary = escapeICalText(row.title ?? "Booking");
  const description = escapeICalText("Booked via your profile");
  const content = `BEGIN:VCALENDAR\nVERSION:2.0\nBEGIN:VEVENT\nDTSTART:${fmt(start)}\nDTEND:${fmt(end)}\nSUMMARY:${summary}\nDESCRIPTION:${description}\nEND:VEVENT\nEND:VCALENDAR`;
  const blob = new Blob([content], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${(row.title ?? "booking").replace(/\s+/g, "_")}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function StatsGrid({
  bundles,
  bookings,
  products,
  giftcards,
}: {
  bundles: PurchaseRow[];
  bookings: PurchaseRow[];
  products: PurchaseRow[];
  giftcards: PurchaseRow[];
}) {
  const stats = [
    { label: "Bundles", count: bundles.length, icon: Package, color: "text-purple-600 bg-purple-50" },
    { label: "Bookings", count: bookings.length, icon: Ticket, color: "text-blue-600 bg-blue-50" },
    { label: "Products", count: products.length, icon: ShoppingBag, color: "text-green-600 bg-green-50" },
    { label: "Giftcards", count: giftcards.length, icon: Gift, color: "text-pink-600 bg-pink-50" },
  ];
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
      {stats.map((stat) => (
        <div key={stat.label} className={`flex items-center gap-3 rounded-lg border p-3 ${stat.color.split(" ")[1]}`}>
          <stat.icon className={`h-5 w-5 ${stat.color.split(" ")[0]}`} />
          <div>
            <p className="text-2xl font-bold">{stat.count}</p>
            <p className={`text-xs ${stat.color.split(" ")[0]}`}>{stat.label}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

function UpcomingSessionsCard({ sessions, manageUrl }: { sessions: PurchaseRow[]; manageUrl: ManageUrl }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Calendar className="h-5 w-5" />
          Upcoming Sessions
        </CardTitle>
      </CardHeader>
      <CardContent>
        {sessions.length === 0 ? (
          <p className="text-sm text-muted-foreground">No upcoming sessions scheduled.</p>
        ) : (
          <div className="space-y-4">
            {sessions.map((session) => (
              <div
                key={session._id}
                className="flex flex-col gap-3 rounded-lg bg-muted/50 p-3 md:flex-row md:items-center md:justify-between"
              >
                <div className="flex-1">
                  <h3 className="font-semibold">{session.title}</h3>
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                    <span>{formatDateWithWeekday(session.eventDate)}</span>
                    <span>•</span>
                    <span>{formatTime(session.eventDate)}</span>
                  </div>
                  <p className="mt-1 text-xs text-primary">{formatRelative(session.eventDate)}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" onClick={() => window.open(generateGoogleCalendarUrl(session), "_blank")} className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    <span className="hidden sm:inline">Google</span>
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => downloadICS(session)} className="flex items-center gap-1">
                    <ExternalLink className="h-3 w-3" />
                    <span className="hidden sm:inline">iCal</span>
                  </Button>
                  <Button variant="outline" size="sm" asChild>
                    <a href={manageUrl("bookings", session._id)}>View</a>
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function OverviewTab({
  allOrders,
  isLoading,
  isError,
  onRetry,
  manageUrl,
}: {
  allOrders: PurchaseRow[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  manageUrl: ManageUrl;
}) {
  if (isLoading) return <TabSkeleton rows={4} />;
  if (isError) return <TabError onRetry={onRetry} />;

  const bundles = allOrders.filter((o) => o.type === "Bundle");
  const bookings = allOrders.filter((o) => o.type === "Booking" || o.type === "Private ");
  const products = allOrders.filter((o) => o.type === "Product");
  const giftcards = allOrders.filter((o) => o.type === "Giftcard");

  // `new Date()`, not `Date.now()` — matches the rest of this app's
  // render-time "now" reads (e.g. BundlesTab's getBundleStatus,
  // WhatsOn.tsx:34) and avoids the eslint-plugin-react-hooks purity rule,
  // which specifically flags the `Date.now()` call form as an impure
  // render-time read.
  const now = new Date().getTime();
  const upcomingSessions = bookings
    .filter((b) => b.eventDate && new Date(b.eventDate).getTime() > now)
    .sort((a, b) => new Date(a.eventDate ?? 0).getTime() - new Date(b.eventDate ?? 0).getTime())
    .slice(0, 5);

  const activeBundles = bundles.filter((b) => getBundleStatus(b).isActive);

  return (
    <div className="space-y-6">
      <StatsGrid bundles={bundles} bookings={bookings} products={products} giftcards={giftcards} />
      <UpcomingSessionsCard sessions={upcomingSessions} manageUrl={manageUrl} />
      {activeBundles.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Package className="h-5 w-5" />
              Your Active Bundles
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {activeBundles.map((bundle) => (
                <BundleProgressRow key={bundle._id} bundle={bundle} manageUrl={manageUrl} />
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
